/**
 * UsageAggregationDashboard — operator dashboard joining live counters
 * (WQ-228 emission target) with settled periods (WQ-233 output).
 *
 * WQ-234 (Phase 4.9 α8 — final α). Read-only over `platform_usage_aggregates`
 * + `platform_billing_period_settlements`. No writes, no audit emission.
 *
 * Layout:
 *   1. Filter row (tenant, capability, period preset / custom dates)
 *   2. Summary cards (3): aggregate count, counter total, settlement count
 *   3. Live counters table (50/page, server-side pagination)
 *   4. Settled periods table (50/page, server-side pagination)
 *
 * Wrapped at the BI Command Center tab render site in BiPanelErrorBoundary.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  listUsageAggregates,
  countUsageAggregates,
  listSettlements,
  countSettlements,
  type UsageAggregate,
  type Settlement,
  type UsageAggregateFilters,
  type SettlementFilters,
} from '../../services/billingService';

const PAGE_SIZE = 50;
const FILTER_DEBOUNCE_MS = 300;

const CAPABILITY_OPTIONS = [
  'prediction',
  'automl',
  'monte_carlo',
  'data_quality',
  'fairness',
  'explainability',
  'dataset_composition',
  'experiment',
  'algorithm',
  'connector',
  'skill',
];

type PeriodPreset = 'last_7d' | 'last_30d' | 'current_month' | 'last_month' | 'custom';

interface FilterState {
  tenant_id: string;
  capability_type: string;
  period_preset: PeriodPreset;
  period_start_after: string; // ISO; only used when preset === 'custom'
  period_end_before: string;
}

const DEFAULT_FILTERS: FilterState = {
  tenant_id: '',
  capability_type: '',
  period_preset: 'last_30d',
  period_start_after: '',
  period_end_before: '',
};

function startOfDayUtc(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}

function startOfMonthUtc(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
}

function deriveBackendFilters(state: FilterState): {
  tenant_id?: string;
  capability_type?: string;
  period_start_after?: string;
  period_end_before?: string;
} {
  const out: ReturnType<typeof deriveBackendFilters> = {};
  if (state.tenant_id) out.tenant_id = state.tenant_id;
  if (state.capability_type) out.capability_type = state.capability_type;

  const now = new Date();
  switch (state.period_preset) {
    case 'last_7d': {
      const start = startOfDayUtc(new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000));
      out.period_start_after = start.toISOString();
      break;
    }
    case 'last_30d': {
      const start = startOfDayUtc(new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000));
      out.period_start_after = start.toISOString();
      break;
    }
    case 'current_month': {
      out.period_start_after = startOfMonthUtc(now).toISOString();
      break;
    }
    case 'last_month': {
      const lastMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
      const thisMonth = startOfMonthUtc(now);
      out.period_start_after = lastMonth.toISOString();
      out.period_end_before = thisMonth.toISOString();
      break;
    }
    case 'custom': {
      if (state.period_start_after) out.period_start_after = state.period_start_after;
      if (state.period_end_before) out.period_end_before = state.period_end_before;
      break;
    }
  }
  return out;
}

const StatusPill: React.FC<{ status: string | null }> = ({ status }) => {
  const v = status ?? '—';
  const cls =
    v === 'settled'
      ? 'badge badge-success'
      : v === 'voided_by_amendment'
      ? 'badge badge-warning'
      : v === 'pending'
      ? 'badge badge-info'
      : 'badge badge-secondary';
  return <span className={cls}>{v}</span>;
};

const fmtDate = (s: string | null | undefined): string =>
  s ? new Date(s).toISOString().slice(0, 19).replace('T', ' ') : '—';

const fmtShortId = (s: string | null | undefined): string =>
  s ? `${s.slice(0, 8)}…` : '—';

const tableHeaderStyle: React.CSSProperties = {
  textAlign: 'left',
  fontSize: 12,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  color: 'var(--text-secondary)',
};

const SummaryCard: React.FC<{
  label: string;
  value: React.ReactNode;
  testId: string;
  hint?: string;
}> = ({ label, value, testId, hint }) => (
  <div
    data-testid={testId}
    style={{
      flex: 1,
      minWidth: 180,
      padding: 16,
      background: 'var(--surface-1, rgba(255,255,255,0.04))',
      border: '1px solid var(--border-subtle, rgba(255,255,255,0.08))',
      borderRadius: 8,
    }}
  >
    <div style={{ fontSize: 11, textTransform: 'uppercase', color: 'var(--text-secondary)' }}>
      {label}
    </div>
    <div style={{ fontSize: 24, fontWeight: 600, marginTop: 4 }}>{value}</div>
    {hint && (
      <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>{hint}</div>
    )}
  </div>
);

const UsageAggregationDashboard: React.FC = () => {
  const { authFetch, isLoading: authLoading } = useAuth();

  const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTERS);

  const [aggregates, setAggregates] = useState<UsageAggregate[]>([]);
  const [aggregateCount, setAggregateCount] = useState<number>(0);
  const [aggregatesLoading, setAggregatesLoading] = useState<boolean>(true);
  const [aggregatesError, setAggregatesError] = useState<string | null>(null);
  const [aggregatesPage, setAggregatesPage] = useState<number>(0);

  const [settlements, setSettlements] = useState<Settlement[]>([]);
  const [settlementCount, setSettlementCount] = useState<number>(0);
  const [settlementsLoading, setSettlementsLoading] = useState<boolean>(true);
  const [settlementsError, setSettlementsError] = useState<string | null>(null);
  const [settlementsPage, setSettlementsPage] = useState<number>(0);

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [debouncedFilters, setDebouncedFilters] = useState<FilterState>(DEFAULT_FILTERS);

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedFilters(filters);
      setAggregatesPage(0);
      setSettlementsPage(0);
    }, FILTER_DEBOUNCE_MS);
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [filters]);

  const backendFilters = useMemo(
    () => deriveBackendFilters(debouncedFilters),
    [debouncedFilters],
  );

  const fetchAggregates = useCallback(async () => {
    setAggregatesLoading(true);
    setAggregatesError(null);
    try {
      const queryFilters: UsageAggregateFilters = {
        ...backendFilters,
        limit: PAGE_SIZE,
        offset: aggregatesPage * PAGE_SIZE,
      };
      const [rows, total] = await Promise.all([
        listUsageAggregates(authFetch, queryFilters),
        countUsageAggregates(authFetch, backendFilters),
      ]);
      setAggregates(rows);
      setAggregateCount(total);
    } catch (err) {
      setAggregatesError(err instanceof Error ? err.message : 'Failed to load aggregates');
      setAggregates([]);
    } finally {
      setAggregatesLoading(false);
    }
  }, [authFetch, backendFilters, aggregatesPage]);

  const fetchSettlements = useCallback(async () => {
    setSettlementsLoading(true);
    setSettlementsError(null);
    try {
      const queryFilters: SettlementFilters = {
        ...backendFilters,
        limit: PAGE_SIZE,
        offset: settlementsPage * PAGE_SIZE,
      };
      const [rows, total] = await Promise.all([
        listSettlements(authFetch, queryFilters),
        countSettlements(authFetch, backendFilters),
      ]);
      setSettlements(rows);
      setSettlementCount(total);
    } catch (err) {
      setSettlementsError(err instanceof Error ? err.message : 'Failed to load settlements');
      setSettlements([]);
    } finally {
      setSettlementsLoading(false);
    }
  }, [authFetch, backendFilters, settlementsPage]);

  useEffect(() => {
    if (authLoading) return;
    fetchAggregates();
  }, [authLoading, fetchAggregates]);

  useEffect(() => {
    if (authLoading) return;
    fetchSettlements();
  }, [authLoading, fetchSettlements]);

  const counterTotal = useMemo(
    () => aggregates.reduce((sum, a) => sum + (a.counter_value || 0), 0),
    [aggregates],
  );

  const handleFilterChange = <K extends keyof FilterState>(key: K, value: FilterState[K]) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <div data-testid="usage-aggregation-dashboard" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Filter row (sticky-top) */}
      <div
        style={{
          display: 'flex',
          gap: 12,
          alignItems: 'center',
          flexWrap: 'wrap',
          padding: 12,
          background: 'var(--surface-1, rgba(255,255,255,0.04))',
          border: '1px solid var(--border-subtle, rgba(255,255,255,0.08))',
          borderRadius: 8,
          position: 'sticky',
          top: 0,
          zIndex: 1,
        }}
      >
        <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
          Tenant:
          <input
            data-testid="filter-tenant"
            type="text"
            placeholder="tenant uuid"
            value={filters.tenant_id}
            onChange={(e) => handleFilterChange('tenant_id', e.target.value)}
            style={{ minWidth: 220 }}
          />
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
          Capability:
          <select
            data-testid="filter-capability"
            value={filters.capability_type}
            onChange={(e) => handleFilterChange('capability_type', e.target.value)}
          >
            <option value="">all</option>
            {CAPABILITY_OPTIONS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
          Period:
          <select
            data-testid="filter-period-preset"
            value={filters.period_preset}
            onChange={(e) => handleFilterChange('period_preset', e.target.value as PeriodPreset)}
          >
            <option value="last_7d">Last 7 days</option>
            <option value="last_30d">Last 30 days</option>
            <option value="current_month">Current month</option>
            <option value="last_month">Last month</option>
            <option value="custom">Custom</option>
          </select>
        </label>
        {filters.period_preset === 'custom' && (
          <>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
              From:
              <input
                data-testid="filter-date-from"
                type="datetime-local"
                value={filters.period_start_after}
                onChange={(e) => handleFilterChange('period_start_after', e.target.value)}
              />
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
              To:
              <input
                data-testid="filter-date-to"
                type="datetime-local"
                value={filters.period_end_before}
                onChange={(e) => handleFilterChange('period_end_before', e.target.value)}
              />
            </label>
          </>
        )}
      </div>

      {/* Summary cards */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <SummaryCard
          label="Active Aggregates"
          value={aggregateCount.toLocaleString()}
          testId="summary-card-aggregates"
          hint={aggregateCount > aggregates.length ? `${aggregates.length} loaded` : undefined}
        />
        <SummaryCard
          label="Counter Total (loaded)"
          value={counterTotal.toLocaleString()}
          testId="summary-card-counter-total"
          hint={aggregateCount > aggregates.length ? '+ more on later pages' : undefined}
        />
        <SummaryCard
          label="Settlements"
          value={settlementCount.toLocaleString()}
          testId="summary-card-settlements"
        />
      </div>

      {/* Live counters table */}
      <section>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8 }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>Live Counters</h3>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            Showing {aggregates.length} of {aggregateCount}
          </span>
        </div>
        {aggregatesError && (
          <div data-testid="aggregates-error" role="alert" style={{ padding: 12, background: 'rgba(220,38,38,0.1)', border: '1px solid rgba(220,38,38,0.4)', borderRadius: 6, marginBottom: 8 }}>
            <div style={{ fontSize: 13, color: 'var(--danger, #dc2626)' }}>{aggregatesError}</div>
            <button
              data-testid="aggregates-error-retry"
              className="btn btn-ghost btn-sm"
              onClick={fetchAggregates}
              style={{ marginTop: 6 }}
            >
              Retry
            </button>
          </div>
        )}
        {aggregatesLoading && !aggregatesError && (
          <div data-testid="aggregates-loading" style={{ padding: 12, fontSize: 13, color: 'var(--text-secondary)' }}>
            Loading…
          </div>
        )}
        {!aggregatesLoading && !aggregatesError && aggregates.length === 0 && (
          <div data-testid="aggregates-empty" style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary)' }}>
            No usage counters match the current filters. Try widening the period or clearing capability/tenant filters.
          </div>
        )}
        {aggregates.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table data-testid="aggregates-table" className="data-table" style={{ width: '100%', fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={tableHeaderStyle}>Tenant</th>
                  <th style={tableHeaderStyle}>Capability</th>
                  <th style={tableHeaderStyle}>Period Start</th>
                  <th style={tableHeaderStyle}>Counter</th>
                  <th style={tableHeaderStyle}>Last Event</th>
                  <th style={tableHeaderStyle}>Allocation</th>
                </tr>
              </thead>
              <tbody>
                {aggregates.map((a) => (
                  <tr key={a.id} data-testid={`aggregates-row-${a.id}`}>
                    <td>{fmtShortId(a.tenant_id)}</td>
                    <td>{a.capability_type}</td>
                    <td>{fmtDate(a.period_start_at)}</td>
                    <td>{a.counter_value.toLocaleString()}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{fmtDate(a.last_event_at)}</td>
                    <td style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{fmtShortId(a.allocation_id)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {aggregateCount > PAGE_SIZE && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, justifyContent: 'flex-end' }}>
            <button
              data-testid="aggregates-pagination-prev"
              className="btn btn-ghost btn-sm"
              disabled={aggregatesPage === 0}
              onClick={() => setAggregatesPage((p) => Math.max(0, p - 1))}
            >
              Prev
            </button>
            <span data-testid="aggregates-page-info" style={{ fontSize: 12 }}>
              Page {aggregatesPage + 1} / {Math.max(1, Math.ceil(aggregateCount / PAGE_SIZE))}
            </span>
            <button
              data-testid="aggregates-pagination-next"
              className="btn btn-ghost btn-sm"
              disabled={(aggregatesPage + 1) * PAGE_SIZE >= aggregateCount}
              onClick={() => setAggregatesPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        )}
      </section>

      {/* Settled periods table */}
      <section>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8 }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>Settled Periods</h3>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            Showing {settlements.length} of {settlementCount}
          </span>
        </div>
        {settlementsError && (
          <div data-testid="settlements-error" role="alert" style={{ padding: 12, background: 'rgba(220,38,38,0.1)', border: '1px solid rgba(220,38,38,0.4)', borderRadius: 6, marginBottom: 8 }}>
            <div style={{ fontSize: 13, color: 'var(--danger, #dc2626)' }}>{settlementsError}</div>
            <button
              data-testid="settlements-error-retry"
              className="btn btn-ghost btn-sm"
              onClick={fetchSettlements}
              style={{ marginTop: 6 }}
            >
              Retry
            </button>
          </div>
        )}
        {settlementsLoading && !settlementsError && (
          <div data-testid="settlements-loading" style={{ padding: 12, fontSize: 13, color: 'var(--text-secondary)' }}>
            Loading…
          </div>
        )}
        {!settlementsLoading && !settlementsError && settlements.length === 0 && (
          <div data-testid="settlements-empty" style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary)' }}>
            No settlement records match the current filters. Closed periods are populated by POST /api/admin/ai/billing/settle-period.
          </div>
        )}
        {settlements.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table data-testid="settlements-table" className="data-table" style={{ width: '100%', fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={tableHeaderStyle}>Tenant</th>
                  <th style={tableHeaderStyle}>Capability</th>
                  <th style={tableHeaderStyle}>Period</th>
                  <th style={tableHeaderStyle}>Counter</th>
                  <th style={tableHeaderStyle}>Quota</th>
                  <th style={tableHeaderStyle}>Tier</th>
                  <th style={tableHeaderStyle}>Status</th>
                  <th style={tableHeaderStyle}>Settled At</th>
                  <th style={tableHeaderStyle}>Settled By</th>
                </tr>
              </thead>
              <tbody>
                {settlements.map((s) => (
                  <tr key={s.id} data-testid={`settlements-row-${s.id}`}>
                    <td>{fmtShortId(s.tenant_id)}</td>
                    <td>{s.capability_type}</td>
                    <td style={{ fontSize: 12 }}>
                      {fmtDate(s.period_start)} → {fmtDate(s.period_end)}
                    </td>
                    <td>{s.counter_value_snapshot.toLocaleString()}</td>
                    <td>{s.quota_snapshot != null ? s.quota_snapshot.toLocaleString() : '—'}</td>
                    <td>{s.tier_snapshot ?? '—'}</td>
                    <td>
                      <StatusPill status={s.settlement_status} />
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{fmtDate(s.settled_at)}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                      {s.settled_by_actor_id ? fmtShortId(s.settled_by_actor_id) : '—'}
                      {s.settled_by_actor_type ? ` (${s.settled_by_actor_type})` : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {settlementCount > PAGE_SIZE && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, justifyContent: 'flex-end' }}>
            <button
              data-testid="settlements-pagination-prev"
              className="btn btn-ghost btn-sm"
              disabled={settlementsPage === 0}
              onClick={() => setSettlementsPage((p) => Math.max(0, p - 1))}
            >
              Prev
            </button>
            <span data-testid="settlements-page-info" style={{ fontSize: 12 }}>
              Page {settlementsPage + 1} / {Math.max(1, Math.ceil(settlementCount / PAGE_SIZE))}
            </span>
            <button
              data-testid="settlements-pagination-next"
              className="btn btn-ghost btn-sm"
              disabled={(settlementsPage + 1) * PAGE_SIZE >= settlementCount}
              onClick={() => setSettlementsPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        )}
      </section>
    </div>
  );
};

export default UsageAggregationDashboard;
